import React, { useState } from 'react';
import { Button } from '../../design-system/Button';
import { Input } from '../../design-system/Input';
import { coachOnboardingAPI } from '../../../api/coachApi';
import { ProfessionalInfoFormData, Certification, Education } from '../../../types/coach';

interface ProfessionalInfoStepProps {
  onComplete: () => void;
  onSkip?: () => void;
  progress?: any;
}

const ProfessionalInfoStep: React.FC<ProfessionalInfoStepProps> = ({ onComplete }) => {
  const [formData, setFormData] = useState<ProfessionalInfoFormData>({
    certifications: [],
    experience_years: 0,
    education: [],
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Form state for adding certifications
  const [newCert, setNewCert] = useState<Partial<Certification>>({
    name: '',
    issuer: '',
    date_obtained: '',
  });
  
  // Form state for adding education
  const [newEducation, setNewEducation] = useState<Partial<Education>>({
    degree: '',
    institution: '',
    year: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (formData.certifications.length === 0) {
      setError('Please add at least one certification');
      return;
    }

    try {
      setLoading(true);
      setError('');
      await coachOnboardingAPI.updateProfessionalInfo(formData);
      onComplete();
    } catch (err: any) {
      setError(err.message || 'Failed to update information');
    } finally {
      setLoading(false);
    }
  };

  const addCertification = () => {
    if (newCert.name && newCert.issuer && newCert.date_obtained) {
      setFormData({
        ...formData,
        certifications: [...formData.certifications, newCert as Certification],
      });
      setNewCert({ name: '', issuer: '', date_obtained: '' });
    }
  };

  const removeCertification = (index: number) => {
    setFormData({
      ...formData,
      certifications: formData.certifications.filter((_, i) => i !== index),
    });
  };

  const addEducation = () => {
    if (newEducation.degree && newEducation.institution && newEducation.year) {
      setFormData({
        ...formData,
        education: [...(formData.education || []), newEducation as Education],
      });
      setNewEducation({ degree: '', institution: '', year: '' });
    }
  };

  const removeEducation = (index: number) => {
    setFormData({
      ...formData,
      education: formData.education?.filter((_, i) => i !== index) || [],
    });
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-6">
      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700">
          {error}
        </div>
      )}

      {/* Years of Experience */}
      <div>
        <label htmlFor="experience" className="block text-sm font-medium text-gray-700 mb-2">
          Years of Experience *
        </label>
        <Input
          id="experience"
          type="number"
          min="0"
          max="50"
          value={formData.experience_years}
          onChange={(e) => setFormData({ ...formData, experience_years: parseInt(e.target.value) || 0 })}
          className="w-32"
        />
      </div>

      {/* Certifications */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Certifications *
        </label>
        <p className="text-sm text-gray-500 mb-4">
          Add your professional certifications and credentials
        </p>
        
        <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              placeholder="Certification name"
              value={newCert.name || ''}
              onChange={(e) => setNewCert({ ...newCert, name: e.target.value })}
            />
            <Input
              placeholder="Issuing organization"
              value={newCert.issuer || ''}
              onChange={(e) => setNewCert({ ...newCert, issuer: e.target.value })}
            />
            <div className="flex gap-2">
              <Input
                type="date"
                placeholder="Date obtained"
                value={newCert.date_obtained || ''}
                onChange={(e) => setNewCert({ ...newCert, date_obtained: e.target.value })}
                className="flex-1"
              />
              <Button type="button" onClick={addCertification} size="sm">
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Certifications List */}
        {formData.certifications.length > 0 && (
          <div className="mt-4 space-y-2">
            {formData.certifications.map((cert, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{cert.name}</p>
                  <p className="text-sm text-gray-600">
                    {cert.issuer} • Obtained {new Date(cert.date_obtained).toLocaleDateString()}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeCertification(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Education */}
      <div>
        <label className="block text-sm font-medium text-gray-700 mb-2">
          Education (Optional)
        </label>
        <p className="text-sm text-gray-500 mb-4">
          Add your educational background
        </p>
        
        <div className="space-y-3 bg-gray-50 p-4 rounded-lg">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <Input
              placeholder="Degree/Certificate"
              value={newEducation.degree || ''}
              onChange={(e) => setNewEducation({ ...newEducation, degree: e.target.value })}
            />
            <Input
              placeholder="Institution"
              value={newEducation.institution || ''}
              onChange={(e) => setNewEducation({ ...newEducation, institution: e.target.value })}
            />
            <div className="flex gap-2">
              <Input
                placeholder="Year"
                value={newEducation.year || ''}
                onChange={(e) => setNewEducation({ ...newEducation, year: e.target.value })}
                className="flex-1"
              />
              <Button type="button" onClick={addEducation} size="sm">
                Add
              </Button>
            </div>
          </div>
        </div>

        {/* Education List */}
        {formData.education && formData.education.length > 0 && (
          <div className="mt-4 space-y-2">
            {formData.education.map((edu, index) => (
              <div
                key={index}
                className="flex items-center justify-between p-3 bg-white border border-gray-200 rounded-lg"
              >
                <div>
                  <p className="font-medium text-gray-900">{edu.degree}</p>
                  <p className="text-sm text-gray-600">
                    {edu.institution} • {edu.year}
                  </p>
                </div>
                <button
                  type="button"
                  onClick={() => removeEducation(index)}
                  className="text-red-600 hover:text-red-700"
                >
                  Remove
                </button>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Submit Button */}
      <div className="flex justify-end pt-6">
        <Button type="submit" isLoading={loading} size="lg">
          Continue
        </Button>
      </div>
    </form>
  );
};

export default ProfessionalInfoStep;